import { useState } from 'react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { generateProductDescription } from '../../services/openai';
import { useUsage } from '../../contexts/UsageContext';

const toneOptions = ['informative', 'fun', 'luxurious', 'casual', 'professional', 'friendly', 'technical'];
const categoryOptions = [
  'Electronics & Gadgets',
  'Fashion & Apparel',
  'Home & Kitchen',
  'Beauty & Personal Care',
  'Sports & Fitness',
  'Health & Wellness',
  'Food & Beverages',
  'Toys & Games',
  'Office Supplies',
  'Pet Supplies'
];
const audienceOptions = [
  'tech enthusiasts',
  'busy professionals',
  'parents',
  'athletes',
  'students',
  'homeowners',
  'outdoor enthusiasts',
  'beauty enthusiasts',
  'health-conscious individuals',
  'luxury shoppers'
];

function ProductDescriptionPrompt() {
  const [formData, setFormData] = useState({
    productName: '',
    category: '',
    targetAudience: '',
    keyFeatures: '',
    benefits: '',
    tone: '',
    additionalDetails: ''
  });
  
  const [generatedContent, setGeneratedContent] = useState('');
  const [query, setQuery] = useState('');
  const [isOpenTone, setIsOpenTone] = useState(false);
  const [isOpenCategory, setIsOpenCategory] = useState(false);
  const [isOpenAudience, setIsOpenAudience] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const { usageStatus, showAd, checkAndRecordRequest } = useUsage();

  const handleShowAd = () => {
    console.log('handleShowAd clicked'); // Debug log
    showAd();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleGenerateDescription = async () => {
    if (!formData.productName || !formData.keyFeatures) {
      setError('Please fill in all required fields');
      return;
    }

    if (!checkAndRecordRequest()) {
      setError('Usage limit reached');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const content = await generateProductDescription(formData);
      setGeneratedContent(content);
    } catch (error) {
      setError('Failed to generate description. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Product Description Generator</h1>
        
        <div className="prose prose-lg text-gray-600 mb-8">
          I want to create an engaging and persuasive product description that highlights the key features and benefits of my product while appealing to my target audience. Here's some context to guide the description:
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Product Name: <span className="text-red-500">*</span></label>
            <div className="flex items-center">
              <input
                type="text"
                className="flex-1 px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none"
                value={formData.productName}
                onChange={(e) => handleInputChange('productName', e.target.value)}
                placeholder="What is the product, and what type is it? E.g., a smartwatch, skincare cream, kitchen gadget, etc."
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Category:</label>
            <div className="relative">
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none"
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  onFocus={() => setIsOpenCategory(true)}
                  onBlur={(e) => {
                    setTimeout(() => setIsOpenCategory(false), 200);
                  }}
                  placeholder="Select a product category"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsOpenCategory(!isOpenCategory);
                  }}
                  className="absolute inset-y-0 right-0 flex items-center pr-2"
                >
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </button>
              </div>
              {isOpenCategory && (
                <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {categoryOptions.map((category) => (
                    <li
                      key={category}
                      className={`relative cursor-default select-none py-2 pl-10 pr-4 hover:bg-primary hover:text-white`}
                      onClick={() => {
                        handleInputChange('category', category);
                        setIsOpenCategory(false);
                      }}
                    >
                      <span className="block truncate">
                        {category}
                      </span>
                      {formData.category === category && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Target Audience:</label>
            <div className="relative">
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none"
                  value={formData.targetAudience}
                  onChange={(e) => handleInputChange('targetAudience', e.target.value)}
                  onFocus={() => setIsOpenAudience(true)}
                  onBlur={(e) => {
                    setTimeout(() => setIsOpenAudience(false), 200);
                  }}
                  placeholder="Who is the product for? E.g., tech enthusiasts, busy parents, athletes, etc."
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsOpenAudience(!isOpenAudience);
                  }}
                  className="absolute inset-y-0 right-0 flex items-center pr-2"
                >
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </button>
              </div>
              {isOpenAudience && (
                <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {audienceOptions.map((audience) => (
                    <li
                      key={audience}
                      className={`relative cursor-default select-none py-2 pl-10 pr-4 hover:bg-primary hover:text-white`}
                      onClick={() => {
                        handleInputChange('targetAudience', audience);
                        setIsOpenAudience(false);
                      }}
                    >
                      <span className="block truncate">
                        {audience}
                      </span>
                      {formData.targetAudience === audience && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Key Features: <span className="text-red-500">*</span></label>
            <div className="flex items-center">
              <textarea
                className="flex-1 px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none min-h-[100px] resize-y"
                value={formData.keyFeatures}
                onChange={(e) => handleInputChange('keyFeatures', e.target.value)}
                placeholder="List the main features and specifications of your product. What makes it unique? What problems does it solve?"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Benefits:</label>
            <div className="flex items-center">
              <textarea
                className="flex-1 px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none min-h-[100px] resize-y"
                value={formData.benefits}
                onChange={(e) => handleInputChange('benefits', e.target.value)}
                placeholder="How does your product improve your customer's life? What value does it provide?"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Tone:</label>
            <div className="relative">
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none"
                  value={formData.tone}
                  onChange={(e) => handleInputChange('tone', e.target.value)}
                  onFocus={() => setIsOpenTone(true)}
                  onBlur={(e) => {
                    setTimeout(() => setIsOpenTone(false), 200);
                  }}
                  placeholder="Choose the tone, e.g., informative, fun, luxurious, casual, etc."
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsOpenTone(!isOpenTone);
                  }}
                  className="absolute inset-y-0 right-0 flex items-center pr-2"
                >
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </button>
              </div>
              {isOpenTone && (
                <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {toneOptions.map((tone) => (
                    <li
                      key={tone}
                      className={`relative cursor-default select-none py-2 pl-10 pr-4 hover:bg-primary hover:text-white`}
                      onClick={() => {
                        handleInputChange('tone', tone);
                        setIsOpenTone(false);
                      }}
                    >
                      <span className="block truncate">
                        {tone}
                      </span>
                      {formData.tone === tone && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Additional Details (Optional):</label>
            <div className="flex items-center">
              <input
                type="text"
                className="flex-1 px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none"
                value={formData.additionalDetails}
                onChange={(e) => handleInputChange('additionalDetails', e.target.value)}
                placeholder="Include any unique selling points, branding elements, or other specific information"
              />
            </div>
          </div>

          <div className="pt-6">
            <div className="flex items-center justify-between">
              <button
                className="w-full px-4 py-2 text-white bg-primary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                onClick={handleGenerateDescription}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generating...
                  </>
                ) : (
                  'Generate Description'
                )}
              </button>
              {usageStatus.allowed && (
                <span className="ml-4 text-sm text-gray-600">
                  Requests remaining before ad: {usageStatus.requestsLeft}
                </span>
              )}
            </div>

            {!usageStatus.allowed && usageStatus.reason === 'cooldown' && (
              <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="flex justify-between items-center">
                  <div className="flex-1">
                    <p className="text-yellow-700">
                      Usage limit reached. Please wait {Math.ceil(usageStatus.remainingTime / 1000)} seconds or view an ad to help support this site.
                    </p>
                  </div>
                  <button
                    onClick={handleShowAd}
                    className="ml-4 px-4 py-2 bg-yellow-100 text-yellow-700 hover:bg-yellow-200 rounded-md flex items-center whitespace-nowrap"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    View Ad to Continue
                  </button>
                </div>
              </div>
            )}

            {!usageStatus.allowed && usageStatus.reason === 'limit' && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-yellow-800 mb-2">Usage limit reached. Please wait {usageStatus.waitTime} seconds or view an ad to continue.</p>
                <button
                  type="button"
                  onClick={handleShowAd}
                  className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
                >
                  View Ad to Continue
                </button>
              </div>
            )}
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
              {error}
            </div>
          )}

          <div className="mt-6">
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">Generated Description:</label>
              {generatedContent && (
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-3 py-1.5 text-gray-500 hover:text-gray-700 bg-white rounded-md hover:bg-gray-100 transition-colors border border-gray-300"
                  title="Copy to clipboard"
                >
                  <ClipboardDocumentIcon className="h-5 w-5" />
                  <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
                </button>
              )}
            </div>
            <div className="w-full h-[400px] p-4 bg-white border border-gray-300 rounded-md overflow-y-auto">
              <pre className="whitespace-pre-wrap font-sans text-gray-800">
                {generatedContent}
              </pre>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductDescriptionPrompt;
